import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';
import PageShell from '../components/site/PageShell';
import { images } from '../config/esanzaContent';
import { company, status } from '../config/company';
import {
  positioning,
  ctas,
  ecosystem,
  pillars,
  audiences,
  traceChain,
  operatingFlow,
} from '../config/product';
import { API_URL } from '../lib/api';
import { containerVariants, itemVariants } from '../utils/animations';

interface LiveListing {
  id: number;
  title: string;
  price_per_unit: number;
  currency: string;
  commercial_unit: string;
  image_url: string;
  supplier_name: string;
  supplier_country?: string;
  available_quantity: number;
  traceability_enabled: boolean;
}

const ModuleIcon: React.FC<{ name: keyof typeof MODULE_PATHS }> = ({ name }) => (
  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.6}>
    <path strokeLinecap="round" strokeLinejoin="round" d={MODULE_PATHS[name]} />
  </svg>
);

const MODULE_PATHS = {
  farm: 'M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21',
  ai: 'M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456z',
  trace: 'M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75A2.25 2.25 0 004.5 12.75v6.75a2.25 2.25 0 002.25 2.25z',
  supply: 'M20.25 7.5l-.625 10.632a2.25 2.25 0 01-2.247 2.118H6.622a2.25 2.25 0 01-2.247-2.118L3.75 7.5M10 11.25h4M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z',
  market: 'M2.25 3h1.386c.51 0 .955.343 1.087.835l.383 1.437M7.5 14.25a3 3 0 00-3 3h15.75m-12.75-3h11.218c1.121 0 2.1-.746 2.431-1.854l1.273-4.948A1.125 1.125 0 0019.44 6.5H6.09m11.328 0H6.09M5.25 18h13.5M6.375 21a.75.75 0 11-1.5 0 .75.75 0 011.5 0zm11.25 0a.75.75 0 11-1.5 0 .75.75 0 011.5 0z',
  logistics: 'M8.25 18.75a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 01-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 00-3.213-9.193 2.056 2.056 0 00-1.58-.86H14.25M16.5 18.75h-2.25m0-11.177v-.958c0-.568-.422-1.048-.987-1.106a48.554 48.554 0 00-10.026 0 1.106 1.106 0 00-.987 1.106v7.635m12-6.677v6.677m0 4.5v-4.5m0 0h-12',
  analytics: 'M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z',
} as const;

const PILLAR_ICONS: Record<string, keyof typeof MODULE_PATHS> = {
  'farm-operations': 'farm',
  'field-intelligence': 'ai',
  traceability: 'trace',
  'supply-chain': 'supply',
  marketplace: 'market',
  logistics: 'logistics',
};

const Home: React.FC = () => {
  const [liveListings, setLiveListings] = useState<LiveListing[]>([]);
  const [loadingListings, setLoadingListings] = useState(true);

  // Only a small slice of the public catalogue is fetched for the market
  // section — the homepage never loads the whole marketplace dataset.
  useEffect(() => {
    let alive = true;
    fetch(`${API_URL}/api/marketplace/public/listings?limit=6&sort=created_at&order=DESC`)
      .then((r) => r.json())
      .then((data) => {
        if (alive && data?.items) setLiveListings(data.items);
      })
      .catch(() => undefined)
      .finally(() => {
        if (alive) setLoadingListings(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  return (
    <>
      <SEO
        title="eSanza | AI Agricultural Operating System"
        description="eSanza is an AI-enabled agricultural operating system for farm management, field intelligence, traceability, supply-chain operations and B2B market access."
        imagePath={images.tree}
        keywords="agricultural operating system, farm management software, AI agriculture platform, AI agronomy assistant, farm traceability software, agricultural supply chain, B2B agricultural marketplace, digital agriculture, precision agriculture, agricultural field intelligence, farm GIS, agricultural procurement, farm analytics"
        url="https://esanza.com"
      />

      <PageShell>
        {/* ─── A. Hero ──────────────────────────────────────────────── */}
        <section className="relative overflow-hidden bg-[#06150e] pb-24 pt-32 md:pb-32 md:pt-40">
          <img src={images.tree} alt="" className="absolute inset-0 h-full w-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-[linear-gradient(100deg,#06150e_0%,rgba(6,21,15,.92)_38%,rgba(6,21,15,.42)_78%,rgba(6,21,15,.28)_100%)]" />
          <div className="absolute -top-40 right-[-10%] h-[520px] w-[520px] rounded-full bg-[#d4af37]/20 blur-[140px]" />
          <div className="absolute bottom-[-30%] left-[-12%] h-[460px] w-[460px] rounded-full bg-[#14532d]/40 blur-[160px]" />

          <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              className="max-w-3xl"
            >
              <span className="inline-flex items-center gap-2 rounded-full border border-[#d4af37]/35 bg-[#d4af37]/10 px-4 py-2 text-xs font-extrabold uppercase tracking-[0.24em] text-[#f3d978]">
                <span className="h-1.5 w-1.5 rounded-full bg-[#d4af37] shadow-[0_0_12px_#d4af37]" />
                {positioning.eyebrow}
              </span>
              <h1 className="mt-7 max-w-4xl text-5xl font-black leading-[0.96] tracking-tight text-white md:text-7xl">
                Manage agriculture{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#f3d978] to-[#d4af37]">
                  from field to market.
                </span>
              </h1>
              <p className="mt-7 max-w-2xl text-lg leading-8 text-white/75 md:text-xl">
                {positioning.supporting}
              </p>
              <div className="mt-10 flex flex-col gap-4 sm:flex-row sm:items-center">
                <Link
                  to={ctas.primary.to}
                  className="group inline-flex items-center justify-center gap-3 rounded-full bg-[#14532d] px-8 py-4 text-base font-black text-white shadow-xl shadow-[#14532d]/25 transition hover:-translate-y-0.5 hover:bg-[#184f2d] hover:shadow-2xl hover:shadow-[#14532d]/30"
                >
                  {ctas.primary.label}
                  <span className="transition group-hover:translate-x-1">→</span>
                </Link>
                <Link
                  to={ctas.secondary.to}
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-white/18 bg-white/[0.06] px-8 py-4 text-base font-bold text-white backdrop-blur transition hover:bg-white/10"
                >
                  {ctas.secondary.label}
                </Link>
                <Link
                  to={ctas.tertiary.to}
                  className="inline-flex items-center justify-center gap-2 rounded-full px-2 py-4 text-sm font-bold text-[#f3d978] underline-offset-4 transition hover:underline"
                >
                  {ctas.tertiary.label}
                </Link>
              </div>

              <p className="mt-9 text-sm font-semibold uppercase tracking-[0.2em] text-white/55">
                {positioning.origin}
              </p>
            </motion.div>
          </div>
        </section>

        {/* ─── B. Platform ecosystem ────────────────────────────────── */}
        <section className="bg-white px-5 py-20 lg:px-8">
          <div className="mx-auto max-w-7xl">
            <motion.div
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: '-80px' }}
              className="max-w-3xl"
            >
              <motion.p variants={itemVariants} className="text-xs font-extrabold uppercase tracking-[0.24em] text-[#987a1f]">
                The platform
              </motion.p>
              <motion.h2 variants={itemVariants} className="mt-4 text-4xl font-black tracking-tight text-[#102016] md:text-5xl">
                One connected agricultural operating system.
              </motion.h2>
              <motion.p variants={itemVariants} className="mt-5 text-base leading-7 text-[#596959]">
                Field data becomes intelligence, intelligence becomes action, action becomes evidence — and evidence becomes supply
                buyers can trust.
              </motion.p>
            </motion.div>

            <motion.ol
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: '-60px' }}
              className="mt-10 grid gap-3 sm:grid-cols-2 lg:grid-cols-4"
            >
              {ecosystem.map((step, i) => (
                <motion.li
                  key={step}
                  variants={itemVariants}
                  className="relative rounded-2xl border border-[#dbe5d2] bg-[#f7f9f4] p-5 transition hover:-translate-y-1 hover:border-[#14532d]/30 hover:shadow-lg"
                >
                  <span className="text-xs font-black tracking-[0.2em] text-[#d4af37]">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <p className="mt-2 text-sm font-black text-[#102016]">{step}</p>
                </motion.li>
              ))}
            </motion.ol>

            <div className="mt-8 overflow-x-auto rounded-2xl border border-[#dbe5d2] bg-[#06150e] px-6 py-5">
              <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-white/40">Operating flow</p>
              <div className="mt-3 flex min-w-max items-center gap-2">
                {operatingFlow.map((step, i) => (
                  <React.Fragment key={step}>
                    <span className="rounded-full border border-[#d4af37]/30 bg-[#d4af37]/10 px-3.5 py-1.5 text-xs font-bold text-[#f3d978]">
                      {step}
                    </span>
                    {i < operatingFlow.length - 1 && <span className="text-white/30">→</span>}
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ─── C. Six product pillars ───────────────────────────────── */}
        <section className="bg-[#f7f9f4] px-5 py-24 lg:px-8">
          <div className="mx-auto max-w-7xl">
            <div className="max-w-3xl">
              <p className="text-xs font-extrabold uppercase tracking-[0.24em] text-[#987a1f]">Capabilities</p>
              <h2 className="mt-4 text-4xl font-black tracking-tight text-[#102016] md:text-5xl">
                Six pillars, one loop.
              </h2>
              <p className="mt-5 text-base leading-7 text-[#596959]">
                Each capability is built on the same records — so nothing has to be entered twice and nothing is disconnected.
              </p>
            </div>

            <div className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
              {pillars.map((pillar) => (
                <motion.article
                  key={pillar.id}
                  variants={itemVariants}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, margin: '-40px' }}
                  className="group flex flex-col rounded-2xl border border-[#dbe5d2] bg-white p-7 shadow-sm transition hover:-translate-y-1 hover:shadow-xl"
                >
                  <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-xl bg-[#14532d] text-[#f3d978] transition group-hover:bg-[#d4af37] group-hover:text-[#06150e]">
                    <ModuleIcon name={PILLAR_ICONS[pillar.id] || 'farm'} />
                  </div>
                  <h3 className="text-xl font-black text-[#102016]">{pillar.title}</h3>
                  <p className="mt-2 text-sm leading-6 text-[#596959]">{pillar.body}</p>
                  <ul className="mt-5 space-y-2 border-t border-[#eef2e8] pt-5">
                    {pillar.points.map((point) => (
                      <li key={point} className="flex items-start gap-2 text-sm text-[#3d4a3d]">
                        <svg className="mt-0.5 h-4 w-4 shrink-0 text-[#14532d]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                        {point}
                      </li>
                    ))}
                  </ul>
                </motion.article>
              ))}
            </div>
          </div>
        </section>

        {/* ─── D. AI intelligence ───────────────────────────────────── */}
        <section className="relative overflow-hidden bg-[#06150e] px-5 py-24 lg:px-8">
          <div className="absolute -left-24 top-10 h-72 w-72 rounded-full bg-[#14532d]/30 blur-[120px]" />
          <div className="absolute -right-20 bottom-10 h-72 w-72 rounded-full bg-[#d4af37]/10 blur-[120px]" />
          <div className="relative mx-auto grid max-w-7xl gap-12 lg:grid-cols-[1fr_1fr] lg:items-center">
            <div>
              <p className="text-xs font-extrabold uppercase tracking-[0.24em] text-[#d4af37]">Field intelligence</p>
              <h2 className="mt-4 text-4xl font-black leading-tight tracking-tight text-white md:text-5xl">
                Agricultural intelligence built into everyday field operations.
              </h2>
              <p className="mt-5 max-w-xl text-base leading-7 text-white/65">
                {positioning.ai} It reads the same weather, crop stage, field history and observations the team already records,
                proposes an action, explains why, and asks an agronomist before anything high-risk proceeds.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link
                  to="/field-intelligence"
                  className="group inline-flex items-center gap-2 rounded-full bg-[#14532d] px-7 py-3.5 text-sm font-bold text-white shadow-lg shadow-[#14532d]/25 transition hover:-translate-y-0.5 hover:bg-[#184f2d]"
                >
                  Explore Field Intelligence
                  <span className="transition group-hover:translate-x-0.5">→</span>
                </Link>
              </div>
            </div>

            <div className="rounded-2xl border border-white/10 bg-white/[0.06] p-6 backdrop-blur">
              <div className="flex items-center justify-between">
                <span className="inline-flex items-center gap-2 rounded-full border border-red-400/40 bg-red-500/15 px-3 py-1.5 text-[11px] font-black uppercase tracking-widest text-red-300">
                  <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m0 3.75h.008v.008H12v-.008z" />
                  </svg>
                  High risk
                </span>
                <span className="text-[11px] font-bold uppercase tracking-widest text-white/40">Advisory preview</span>
              </div>
              <h3 className="mt-4 text-xl font-black text-white">Possible fungal disease risk</h3>

              <div className="mt-5 grid gap-4">
                <div>
                  <p className="text-[11px] font-black uppercase tracking-[0.18em] text-[#d4af37]">Why</p>
                  <ul className="mt-2 space-y-1.5 text-sm text-white/70">
                    <li>• Recent rainfall — 31 mm</li>
                    <li>• High humidity</li>
                    <li>• Field observation reported yesterday</li>
                    <li>• Crop at fruiting stage</li>
                  </ul>
                </div>
                <div>
                  <p className="text-[11px] font-black uppercase tracking-[0.18em] text-[#d4af37]">Recommended action</p>
                  <p className="mt-2 text-sm text-white/70">Inspect the affected area before spraying.</p>
                </div>
                <div>
                  <p className="text-[11px] font-black uppercase tracking-[0.18em] text-[#d4af37]">Sources</p>
                  <p className="mt-2 text-sm text-white/55">
                    Weather — 07 Oct 2026 06:00 · Field observation — 06 Oct 2026 · Crop stage — current record
                  </p>
                </div>
                <div className="rounded-xl border border-[#d4af37]/30 bg-[#d4af37]/10 px-4 py-3 text-sm font-bold text-[#f3d978]">
                  Human review: agronomist review recommended.
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ─── E. For different users ───────────────────────────────── */}
        <section className="bg-white px-5 py-24 lg:px-8">
          <div className="mx-auto max-w-7xl">
            <div className="max-w-3xl">
              <p className="text-xs font-extrabold uppercase tracking-[0.24em] text-[#987a1f]">Who it is for</p>
              <h2 className="mt-4 text-4xl font-black tracking-tight text-[#102016] md:text-5xl">
                Built for everyone who touches the crop.
              </h2>
            </div>
            <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {audiences.map((audience) => (
                <div
                  key={audience.title}
                  className="rounded-2xl border border-[#dbe5d2] bg-[#f7f9f4] p-6 transition hover:-translate-y-1 hover:border-[#14532d]/30 hover:shadow-lg"
                >
                  <h3 className="text-base font-black text-[#102016]">{audience.title}</h3>
                  <p className="mt-2 text-sm leading-6 text-[#596959]">{audience.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ─── F. Traceability chain ────────────────────────────────── */}
        <section className="relative overflow-hidden bg-[#06150e] px-5 py-24 lg:px-8">
          <img src={images.traceabilityPhoto || images.tree} alt="" className="absolute inset-0 h-full w-full object-cover opacity-15" />
          <div className="absolute inset-0 bg-[#06150e]/90" />
          <div className="relative mx-auto max-w-7xl">
            <div className="grid gap-10 lg:grid-cols-[0.8fr_1.2fr] lg:items-center">
              <div>
                <p className="text-xs font-extrabold uppercase tracking-[0.24em] text-[#d4af37]">Traceability</p>
                <h2 className="mt-4 text-4xl font-black leading-tight tracking-tight text-white md:text-5xl">
                  From field activity to final buyer.
                </h2>
                <p className="mt-5 text-base leading-7 text-white/65">
                  Traceability is not a sticker applied at the end. It is the by-product of work the team was already doing —
                  recorded once, reused everywhere.
                </p>
                <Link
                  to="/traceability"
                  className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#14532d] px-7 py-3.5 text-sm font-bold text-white shadow-lg shadow-[#14532d]/25 transition hover:-translate-y-0.5 hover:bg-[#184f2d]"
                >
                  Explore traceability →
                </Link>
              </div>

              <div className="grid gap-3 sm:grid-cols-3">
                {traceChain.map((step, i) => (
                  <div
                    key={step}
                    className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.06] px-4 py-3.5"
                  >
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#14532d] text-[11px] font-black text-[#f3d978]">
                      {i + 1}
                    </span>
                    <span className="text-sm font-bold text-white">{step}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ─── G. Market access ─────────────────────────────────────── */}
        <section className="bg-[#f7f9f4] px-5 py-24 lg:px-8">
          <div className="mx-auto max-w-7xl">
            <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
              <div className="max-w-2xl">
                <p className="text-xs font-extrabold uppercase tracking-[0.24em] text-[#987a1f]">Market access</p>
                <h2 className="mt-4 text-4xl font-black tracking-tight text-[#102016] md:text-5xl">
                  Market access built into the operating system.
                </h2>
                <p className="mt-5 text-base leading-7 text-[#596959]">
                  Verified production records become buyer-ready supply. Quantity, grade and traceability come from the field —
                  suppliers do not retype them to trade.
                </p>
              </div>
              <Link
                to="/marketplace"
                className="inline-flex shrink-0 items-center gap-2 rounded-full bg-[#14532d] px-7 py-3.5 text-sm font-bold text-white shadow-lg shadow-[#14532d]/20 transition hover:-translate-y-0.5 hover:bg-[#184f2d]"
              >
                Explore eSanza Market →
              </Link>
            </div>

            {loadingListings ? (
              <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="animate-pulse rounded-2xl border border-[#dbe5d2] bg-white p-6">
                    <div className="h-4 w-2/3 rounded bg-gray-200" />
                    <div className="mt-3 h-3 w-1/2 rounded bg-gray-200" />
                    <div className="mt-4 h-6 w-1/3 rounded bg-gray-200" />
                  </div>
                ))}
              </div>
            ) : liveListings.length > 0 ? (
              <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {liveListings.map((listing) => (
                  <Link
                    key={listing.id}
                    to={`/marketplace?highlight=${listing.id}`}
                    className="group rounded-2xl border border-[#dbe5d2] bg-white p-5 transition hover:-translate-y-1 hover:shadow-xl"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <h3 className="truncate text-sm font-black text-[#102016]">{listing.title}</h3>
                        <p className="mt-1 truncate text-xs text-[#596959]">
                          {listing.supplier_name}
                          {listing.supplier_country ? ` · ${listing.supplier_country}` : ''}
                        </p>
                      </div>
                      {listing.traceability_enabled && (
                        <span className="shrink-0 rounded-md bg-[#14532d] px-2 py-1 text-[10px] font-bold text-white">
                          Traceable
                        </span>
                      )}
                    </div>
                    <div className="mt-4 flex items-baseline gap-1.5">
                      <span className="text-lg font-black text-[#14532d]">
                        {listing.currency} {Number(listing.price_per_unit).toLocaleString()}
                      </span>
                      <span className="text-xs text-gray-400">/ {listing.commercial_unit}</span>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="mt-10 rounded-3xl border border-[#dbe5d2] bg-white px-6 py-12 text-center">
                <p className="text-sm text-[#596959]">Verified supply from connected operations appears here.</p>
                <Link to="/marketplace" className="mt-4 inline-block text-sm font-bold text-[#14532d] hover:underline">
                  Visit the marketplace →
                </Link>
              </div>
            )}
          </div>
        </section>

        {/* ─── H. Evidence / deployment state ───────────────────────── */}
        <section className="bg-white px-5 py-20 lg:px-8">
          <div className="mx-auto grid max-w-7xl gap-6 md:grid-cols-3">
            {[
              { label: 'Platform', value: status.platform, note: 'Web, dashboard, API and mobile in production use.' },
              { label: 'Deployment', value: status.field, note: 'Rolling out with farms, cooperatives and partners.' },
              { label: 'Origin', value: `Built in ${company.originCountry}.`, note: positioning.originFooter },
            ].map((item) => (
              <div key={item.label} className="rounded-2xl border border-[#dbe5d2] bg-[#f7f9f4] p-7">
                <p className="text-[11px] font-black uppercase tracking-[0.2em] text-[#987a1f]">{item.label}</p>
                <p className="mt-3 text-2xl font-black text-[#102016]">{item.value}</p>
                <p className="mt-2 text-sm leading-6 text-[#596959]">{item.note}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ─── I. Final CTA ─────────────────────────────────────────── */}
        <section className="relative overflow-hidden bg-[#0a1f14] px-5 py-24 lg:px-8">
          <div className="absolute left-1/2 top-0 h-64 w-[720px] -translate-x-1/2 rounded-full bg-[#d4af37]/10 blur-[120px]" />
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
            className="relative mx-auto grid max-w-7xl gap-8 md:grid-cols-[1fr_auto] md:items-center"
          >
            <div>
              <motion.h2 variants={itemVariants} className="max-w-3xl text-4xl font-black tracking-tight text-white md:text-5xl">
                Run your agricultural operations on eSanza.
              </motion.h2>
              <motion.p variants={itemVariants} className="mt-5 max-w-2xl text-base leading-8 text-white/68">
                {positioning.definition}
              </motion.p>
            </div>
            <motion.div variants={itemVariants} className="flex flex-col gap-4 sm:flex-row md:flex-col">
              <Link
                to={ctas.secondary.to}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-[#14532d] px-8 py-4 text-base font-black text-white shadow-xl shadow-[#14532d]/25 transition hover:-translate-y-0.5 hover:bg-[#184f2d]"
              >
                {ctas.secondary.label} <span>→</span>
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-white/18 bg-white/[0.06] px-8 py-4 text-base font-bold text-white backdrop-blur transition hover:bg-white/10"
              >
                Talk to eSanza
              </Link>
              <Link
                to={ctas.primary.to}
                className="inline-flex items-center justify-center gap-2 rounded-full px-8 py-4 text-base font-bold text-[#f3d978] underline-offset-4 transition hover:underline"
              >
                Explore Platform
              </Link>
            </motion.div>
          </motion.div>
        </section>
      </PageShell>
    </>
  );
};

export default Home;
